import _ from 'lodash'
import moment from 'moment'
import { joinHumanList } from '@/util'

import { Enums, Entities } from '@/model/util'
import { BrandEnum } from '@/model/environment'

export const SegmentEnum = Enums.create('segment')

export class Address {
  static local = {
    street: undefined,
    city: undefined,
    administrative_area_1: undefined,
    administrative_area_2: undefined,
    country: undefined
  }

  static fixed = {
    street_number: undefined,
    postal_code: undefined,
    latitude: undefined,
    longitude: undefined
  }

  static read = {
    localized: undefined
  }

  constructor (data) {
    Object.assign(this, Address.local, Address.fixed, Address.read, data)
    if (this.localized) {
      this.localized = new Address(Object.assign({}, _.pick(this, _.keys(Address.local)), data.localized, _.pick(this, _.keys(Address.fixed))))
    }
  }

  get shortName () {
    return joinHumanList([this.streetDisplay, this.city])
  }

  get streetDisplay () {
    return joinHumanList([this.street, this.street_number], ' ')
  }

  get areaDisplay () {
    return joinHumanList([this.administrative_area_1, this.administrative_area_2])
  }

  get cityDisplay () {
    return joinHumanList([this.city, this.postal_code], ' ')
  }

  get coordinates () {
    return _.isUndefined(this.latitude) ? undefined : { latitude: this.latitude, longitude: this.longitude }
  }

  get fullName () {
    return joinHumanList([this.streetDisplay, this.cityDisplay, this.areaDisplay, this.country])
  }

  asUpdate () {
    const result = _.pick(this, _.keys(Address.local))
    Object.assign(result, _.pick(this, _.keys(Address.fixed)))
    if (this.localized) {
      result.localized = _.pick(this.localized, _.keys(Address.local))
    }
    return result
  }
}
export class Company {
  static read = {
    id: undefined,
    created_on: undefined,
    address: undefined
  }

  static update = {
    name: undefined,
    account_number: undefined,
    phone_number: undefined,
    website: undefined,
    email: undefined
  }

  constructor (data) {
    Object.assign(this, Company.read, Company.update, data)
    this.address = this.address && new Address(this.address)
    this.brand = Enums.apply(this.brand, BrandEnum)
  }

  set 'created_on' (date) {
    this._createdOn = date && moment(date)
  }

  get 'created_on' () {
    return this._createdOn
  }

  get fullDisplayName () {
    return this.account_number ? `${this.name} (${this.account_number})` : this.name
  }

  asCreate () {
    return this.asUpdate()
  }

  asUpdate () {
    const update = _.pick(this, _.keys(Company.update))
    update.brand = this.brand && this.brand.id
    update.address = Entities.asUpdate(this.address)
    return update
  }
}

export const CompanyPermissionEnum = Enums.create('companyPermission')

export const ApplicationEnum = Enums.create('application', {
  RECO: Enums.value('product-recommendation', 'application.reco', { icon: 'binoculars', authorized: true, branded: true }),
  BYNDER: Enums.value('bynder', 'application.bynder', { icon: 'photo' })
})

export class ApplicationAccess {
  static read = {
    id: undefined,
    company: undefined,
    user: undefined,
    application: undefined,
    brand: undefined
  }

  constructor (data) {
    Object.assign(this, ApplicationAccess.read, data)
    this.application = Enums.apply(this.application, ApplicationEnum)
    this.brand = Enums.apply(this.brand, BrandEnum)
  }

  static forCompany (company, application, auth, brand) {
    const result = new ApplicationAccess()
    result.company = company
    result.application = application
    result.authorization = auth
    result.brand = brand
    return result
  }

  asCreate () {
    return {
      company_id: this.company && this.company.id,
      user_id: this.user && this.user_id,
      application_account: {
        application: this.application.id,
        authorization: this.authorization,
        brand: this.brand && this.brand.id
      }
    }
  }
}

export class CompanyRelation {
  static read = {
    subject: undefined,
    related: undefined,
    type: undefined
  }

  constructor (data) {
    Object.assign(this, CompanyRelation.read, data)
    this.subject = new Company(this.subject)
    this.related = new Company(this.related)
  }
}

export class CompanyUserPermission {
  static read = {
    id: undefined,
    company: undefined,
    user: undefined,
    permission: undefined
  }

  constructor (data) {
    Object.assign(this, CompanyUserPermission.read, data)
  }

  asCreate () {
    const result = {}
    Entities.addReference(result, this, 'user')
    Entities.addReference(result, this, 'permission')
    return result
  }
}
